import React from 'react';
import styled from 'styled-components';
import { spacing } from '../../spacing';
import colors from '../../colors';

export type AlertVariant = 'info' | 'success' | 'warning' | 'error';

export interface AlertProps {
  variant?: AlertVariant;
  title?: string;
  children: React.ReactNode;
  onClose?: () => void;
  className?: string;
}

const variantColors = {
  info: {
    background: colors.primary[50],
    border: colors.primary[200],
    text: colors.primary[900],
    icon: colors.primary[600],
  },
  success: {
    background: '#F6FFED',
    border: '#B7EB8F',
    text: '#135200',
    icon: colors.semantic.success.default,
  },
  warning: {
    background: '#FFFBE6',
    border: '#FFE58F',
    text: '#614700',
    icon: colors.semantic.warning.default,
  },
  error: {
    background: '#FFF1F0',
    border: '#FFCCC7',
    text: '#5C0011',
    icon: colors.semantic.error.default,
  },
};

const StyledAlert = styled.div<{ $variant: AlertVariant }>`
  padding: ${spacing[12]};
  border-radius: 8px;
  border: 1px solid;
  position: relative;
  
  ${({ $variant }) => {
    const variantColor = variantColors[$variant];
    return `
      background-color: ${variantColor.background};
      border-color: ${variantColor.border};
      color: ${variantColor.text};
    `;
  }}
`;

const AlertTitle = styled.div`
  font-weight: 600;
  margin-bottom: ${spacing[4]};
`;

const AlertContent = styled.div`
  font-size: 14px;
  line-height: 1.5;
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${spacing[8]};
  right: ${spacing[8]};
  background: none;
  border: none;
  padding: ${spacing[4]};
  cursor: pointer;
  color: inherit;
  opacity: 0.6;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
  }
`;

export const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  title,
  children,
  onClose,
  className,
}) => {
  return (
    <StyledAlert $variant={variant} className={className}>
      {title && <AlertTitle>{title}</AlertTitle>}
      <AlertContent>{children}</AlertContent>
      {onClose && (
        <CloseButton onClick={onClose} aria-label="Close alert">
          ×
        </CloseButton>
      )}
    </StyledAlert>
  );
};